import React, { useContext, useEffect, useState } from 'react';
import {
  ConnectSecondarySocialProfilesModalView,
  ConnectSecondarySocialProfilesModalViewProps
} from './ConnectSecondarySocialProfilesModal.view';
import { useForm, UseFormReturn, useWatch, ValidateResult } from 'react-hook-form';
import { FormOptions } from '../../../../constants/form';
import { SubmitHandler } from 'react-hook-form/dist/types/form';
import {
  ConnectSecondarySocialProfilesMutationFn,
  GetQueueMetadataDocument,
  GetSocialProfilesDocument,
  GetSocialProfilesQuery,
  GetSocialProfilesQueryVariables,
  PlanUsageType,
  SecondaryProfileConnectionInput,
  SecondaryProfileType,
  UsageFieldsFragmentDoc
} from '../../../../models/graphql';
import { showToast } from '../../../../helpers/showToast';
import { getApolloErrorMessage, remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { ValidationMessages } from '../../../../constants/validation-messages';
import { SelectAllValidationType } from '../../../../components/actions/SelectAllCheckboxes/SelectAllCheckboxes.view';
import { getApolloError } from '../../../../constants/apollo-errors';
import { ApolloErrorMapKeysType } from '../../../../models/apollo-errors';
import { SelectItem } from '../../../../components/forms/Select/Select.view';
import { Select } from '../../../../components/forms/Select';
import { InfoModalObject } from '../InfoModal/InfoModal.view';
import {
  ExternalSecondaryProfiles_externalSecondaryProfiles,
  ExternalSecondaryProfiles_externalSecondaryProfiles_availablePostAsProfiles,
  GetSocialProfiles_workspace_socialProfiles,
  GetSocialProfiles_workspace_socialProfiles_FacebookSocialProfile,
  GetSocialProfiles_workspace_socialProfiles_InstagramSocialProfile,
  GetSocialProfiles_workspace_socialProfiles_LinkedInSocialProfile
} from '../../../../models/legacy-graphql';
import { useApolloClient } from '@apollo/client';
import { AppContext, socialOAuthVar } from '../../../../providers/AppProvider';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import pluralize from 'pluralize';

export type ConnectSecondarySocialProfilesModalControllerProps = Omit<
  ConnectSecondarySocialProfilesModalViewProps,
  | 'disableSelectAll'
  | 'error'
  | 'formInstance'
  | 'infoModal'
  | 'isPartial'
  | 'postAsSelect'
  | 'selectAllValidation'
  | 'setInfoModal'
  | 'submit'
> & {
  connectSecondarySocialProfilesMutation: ConnectSecondarySocialProfilesMutationFn;
  // extend your ConnectSecondarySocialProfilesModalViewProps
};

export type ISecondaryProfileData = {
  profiles: Array<boolean | string>;
};

export const ConnectSecondarySocialProfilesModalController = (
  props: ConnectSecondarySocialProfilesModalControllerProps
): JSX.Element => {
  const apolloClient = useApolloClient();
  const { setLimitReachedModalState } = useContext(AppContext);
  const { remainingProfiles, getUsageFieldsLazyQuery } = useOrganizationPlan();
  const { cache } = apolloClient;
  const [loading, setLoading] = useState<boolean>(false);
  const [infoModal, setInfoModal] = useState<InfoModalObject | null>(null);
  const formInstance: UseFormReturn = useForm({ ...FormOptions });
  const selectAllValidation = {
    required: ValidationMessages.socialProfiles.required
  } as SelectAllValidationType;

  const watchProfiles: boolean[] | undefined = useWatch({
    control: formInstance.control,
    name: 'profiles' // without supply name will watch the entire form, or ['firstName', 'lastName'] to watch both
  });

  const submit: SubmitHandler<ISecondaryProfileData> = (data, ev) => {
    ev?.preventDefault();
    setLoading(true);
    const profiles: SecondaryProfileConnectionInput[] = [];
    data.profiles.forEach((value, index) => {
      if (value) {
        const foundProfile: ExternalSecondaryProfiles_externalSecondaryProfiles = props.data.profiles[index];
        let postAsExternalId: null | string = null;
        if (foundProfile.availablePostAsProfiles) {
          postAsExternalId = (data as unknown as Record<string, string>)[`postAs-${foundProfile.externalId}`];
        }
        profiles.push({
          externalId: foundProfile.externalId,
          profileType: props.data.type,
          postAsExternalId: postAsExternalId
        });
      }
    });
    if (profiles.length) {
      props
        .connectSecondarySocialProfilesMutation({
          variables: {
            workspaceId: props.data.workspaceId,
            socialAuthToken: props.data.socialOAuth.socialAuthToken,
            profilesNetwork: props.data.socialOAuth.network,
            profiles: profiles
          },
          update: () =>
            apolloClient.refetchQueries({
              include: [GetQueueMetadataDocument]
            })
        })
        .then(data => {
          let results: boolean[] = [];
          const newProfiles: GetSocialProfiles_workspace_socialProfiles[] = [];
          data.data?.connectSecondarySocialProfiles.forEach(profile => {
            const { connected, error, reconnected, additionalReconnection, socialProfile } = profile;
            if (additionalReconnection) {
              return;
            }
            if (connected && !error && !reconnected) {
              newProfiles.push(
                socialProfile as
                  | GetSocialProfiles_workspace_socialProfiles_FacebookSocialProfile
                  | GetSocialProfiles_workspace_socialProfiles_LinkedInSocialProfile
                  | GetSocialProfiles_workspace_socialProfiles_InstagramSocialProfile
              );
              results = [...results, true];
            } else if (error) {
              // error
              results = [...results, false];
            }
          });
          // all profiles have been checked and results are collected
          if (results.length === profiles.length && !results.filter(value => !value).length) {
            const socialProfilesCache = cache.readQuery<GetSocialProfilesQuery, GetSocialProfilesQueryVariables>({
              query: GetSocialProfilesDocument,
              variables: { workspaceId: props.data.workspaceId as string }
            });
            socialProfilesCache &&
              cache.writeQuery<GetSocialProfilesQuery, GetSocialProfilesQueryVariables>({
                query: GetSocialProfilesDocument,
                variables: { workspaceId: props.data.workspaceId as string },
                data: {
                  ...socialProfilesCache,
                  workspace: {
                    ...socialProfilesCache.workspace,
                    socialProfiles: [...socialProfilesCache.workspace.socialProfiles, ...newProfiles]
                  }
                }
              });
            const oldUsage: PlanUsageType | null = cache.readFragment({
              fragment: UsageFieldsFragmentDoc,
              fragmentName: 'UsageFields',
              id: 'PlanUsageType:{}'
            });
            const newUsage = { ...oldUsage, socialProfiles: (oldUsage?.socialProfiles ?? 0) + newProfiles.length };
            cache.writeFragment({
              fragment: UsageFieldsFragmentDoc,
              fragmentName: 'UsageFields',
              id: 'PlanUsageType:{}',
              data: newUsage
            });
            const detail =
              newProfiles.length > 1 ? (
                <>
                  <p className={'no-b-m'}>Social Profiles:</p>
                  <ul className={'no-b-m'}>
                    {newProfiles.map((p, i) => (
                      <li key={i}>{p.displayName}</li>
                    ))}
                  </ul>
                  <p className={'no-b-m'}>have been connected successfully.</p>
                </>
              ) : (
                `Social profile '${newProfiles[0].displayName}' has been connected successfully.`
              );
            showToast({
              summary: `Social ${pluralize('Profile', newProfiles.length)} Connected`,
              severity: 'success',
              detail
            });
            if (profiles.filter(profile => profile.profileType === SecondaryProfileType.GROUP)?.length) {
              setInfoModal({
                type: 'GroupAdded',
                profiles: newProfiles
              });
            }
            // show modal for fb groups
          } else {
            let errorMessage: JSX.Element[] = [];
            results.forEach((value, index) => {
              if (!value) {
                const item = getApolloError(
                  data.data?.connectSecondarySocialProfiles[index].error as ApolloErrorMapKeysType,
                  'connectSecondarySocialProfiles'
                );
                errorMessage = [...errorMessage, item];
              }
            });
            // some or all profiles failed
            // TODO handle this error appropriately
            showToast({
              severity: 'error',
              summary: 'Connect Profile Error',
              detail: errorMessage.map((value, index) => <p key={index}>{value}</p>)
            });
          }
          setLoading(false);
          if (!profiles.filter(profile => profile.profileType === SecondaryProfileType.GROUP)?.length) {
            props.onClose();
          }
        })
        .catch(e => {
          // show toaster
          if (getApolloErrorMessage(e) === 'PLAN_LIMIT_EXCEEDED') {
            getUsageFieldsLazyQuery().then(() => {
              setLoading(false);
              setLimitReachedModalState({
                isOpen: true,
                type: 'profiles',
                remaining: remainingProfiles
              });
            });
          } else {
            setLoading(false);
            showToast({
              severity: 'error',
              summary: 'Connect Profile Error',
              detail: remapApolloErrors(e)
            });
          }
        })
        .finally(() => socialOAuthVar(null));
    } else {
      setLoading(false);
      //set errors
    }
    //submit form
  };

  const postAsSelect = (
    profile: ExternalSecondaryProfiles_externalSecondaryProfiles,
    index: number
  ): JSX.Element | null => {
    if (profile.availablePostAsProfiles === null) {
      return null;
    }
    const filteredProfiles = profile.availablePostAsProfiles;
    //const filteredProfiles = profile.availablePostAsProfiles.filter(profile => !profile.externalId.endsWith('-DELETED'));
    const onlyOne = filteredProfiles?.length === 1 ? filteredProfiles[0].externalId : null;
    const items: SelectItem[] =
      filteredProfiles?.map(postAs => ({
        label: onlyOne ? `Post as: ${postAs.displayName}` : postAs.displayName,
        value: postAs.externalId
      })) || [];

    const postAsRules = {
      validate: {
        parentCheck: (value: string | boolean): ValidateResult => {
          const isSelected: boolean = formInstance.getValues(`profiles.${index}`);
          const postAsName = `postAs-${props.data.profiles[index].externalId}`;
          const postAsValue = formInstance.getValues(postAsName);
          if (!isSelected) {
            if (postAsValue !== undefined) {
              formInstance.setValue(postAsName, undefined, { shouldValidate: false, shouldTouch: false });
            }
            formInstance.clearErrors(postAsName);
            return undefined;
          }
          return !!value ? undefined : ValidationMessages.facebookGroupsPostAs.required;
        }
      }
    };

    const postAsName = `postAs-${profile.externalId}`;

    if (watchProfiles && !watchProfiles[index] && formInstance.formState.errors.hasOwnProperty(postAsName)) {
      formInstance.clearErrors(postAsName);
    }

    return (
      <Select
        {...(!onlyOne
          ? {
              chooseLabel: 'Post as: ',
              readonly: !(watchProfiles && watchProfiles[index])
            }
          : {
              readonly: true,
              selectedValue: onlyOne,
              chooseLabel: `Post as: ${
                profile?.availablePostAsProfiles?.length ? profile.availablePostAsProfiles[0].displayName : ''
              }`
            })}
        disabled={props.loading}
        rules={postAsRules}
        items={items}
        name={postAsName}
        size={'medium'}
        useFormInstance={formInstance}
      />
    );
  };

  const disableSelectAll = !props.data.profiles.filter(profile => profile.organizationId === null).length;

  // watch for checkbox on Profiles select and update value for PostAs
  useEffect(() => {
    if (watchProfiles) {
      watchProfiles.forEach((value, index) => {
        if (value === undefined) return;
        const postAsName = `postAs-${props.data.profiles[index].externalId}`;
        const onlyOnePostAsAvailable: boolean =
          !!props.data.profiles[index].availablePostAsProfiles &&
          (
            props.data.profiles[index]
              .availablePostAsProfiles as ExternalSecondaryProfiles_externalSecondaryProfiles_availablePostAsProfiles[]
          ).length < 2;
        const postAsValue = formInstance.getValues(postAsName);
        if (!value && postAsValue !== undefined && !onlyOnePostAsAvailable) {
          formInstance.setValue(postAsName, undefined, { shouldValidate: false });
        } else if (onlyOnePostAsAvailable && !postAsValue) {
          // if there is only one postAs available, we want to make sure the value is set automatically
          // since select filed is readonly and user can not change it's value
          const value = (
            props.data.profiles[index]
              .availablePostAsProfiles as ExternalSecondaryProfiles_externalSecondaryProfiles_availablePostAsProfiles[]
          )[0].externalId;
          formInstance.setValue(postAsName, `${value}`, {
            shouldValidate: true
          });
        }
      });
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [watchProfiles]);

  const viewProps: ConnectSecondarySocialProfilesModalViewProps = {
    ...props,
    disableSelectAll,
    formInstance,
    infoModal,
    postAsSelect,
    selectAllValidation,
    setInfoModal,
    submit,
    isPartial: false,
    loading
  };
  return <ConnectSecondarySocialProfilesModalView {...viewProps} />;
};
